import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { Booking, BookingRef, BookingCreatedResponse, BookingPosition } from '../models/booking.model';
import { QueueResponse, Salon } from '../models/queue.model';

interface ApiResponse<T> {
  success: boolean;
  data: T;
  error?: string;
}

@Injectable({ providedIn: 'root' })
export class ApiService {
  private http = inject(HttpClient);
  private base = environment.apiUrl;

  getSalon(): Observable<Salon> {
    return this.http.get<ApiResponse<Salon>>(`${this.base}/salon`).pipe(map(r => r.data));
  }

  getQueue(): Observable<QueueResponse> {
    return this.http.get<ApiResponse<QueueResponse>>(`${this.base}/queue`).pipe(map(r => r.data));
  }

  getQueuePosition(ref: BookingRef): Observable<BookingPosition> {
    return this.http.get<ApiResponse<BookingPosition>>(
      `${this.base}/queue/position?num=${ref.num}&secret=${encodeURIComponent(ref.secret)}`
    ).pipe(map(r => r.data));
  }

  createBooking(payload: {
    first_name: string;
    last_name: string;
    email: string;
    mobile: string;
    group_size: number;
    sms_opted_in: boolean;
  }): Observable<BookingCreatedResponse> {
    return this.http.post<ApiResponse<BookingCreatedResponse>>(`${this.base}/booking`, payload).pipe(map(r => r.data));
  }

  getBooking(ref: BookingRef): Observable<Booking> {
    return this.http.get<ApiResponse<Booking>>(
      `${this.base}/booking/${ref.num}?secret=${encodeURIComponent(ref.secret)}`
    ).pipe(map(r => r.data));
  }

  cancelBooking(ref: BookingRef): Observable<{ cancelled: boolean }> {
    return this.http.delete<ApiResponse<{ cancelled: boolean }>>(
      `${this.base}/booking/${ref.num}?secret=${encodeURIComponent(ref.secret)}`
    ).pipe(map(r => r.data));
  }

  registerDeviceToken(ref: BookingRef, token: string, platform: 'ios' | 'android' | 'web'): Observable<void> {
    return this.http.post<ApiResponse<void>>(`${this.base}/device-token`, {
      num: ref.num,
      secret: ref.secret,
      token,
      platform,
    }).pipe(map(() => void 0));
  }

  adminRequestOtp(email: string): Observable<{ sent: boolean }> {
    return this.http.post<ApiResponse<{ sent: boolean }>>(
      `${this.base}/admin-auth/request`, { email }
    ).pipe(map(r => r.data));
  }

  adminVerifyOtp(email: string, otp: string): Observable<{ token: string; fullname: string }> {
    return this.http.post<ApiResponse<{ token: string; fullname: string }>>(
      `${this.base}/admin-auth/verify`, { email, otp }
    ).pipe(map(r => r.data));
  }

  registerAdminToken(jwtToken: string, pushToken: string, platform: 'ios' | 'android' | 'web'): Observable<void> {
    const headers = new HttpHeaders({ Authorization: `Bearer ${jwtToken}` });
    return this.http.post<ApiResponse<void>>(
      `${this.base}/admin-token`, { token: pushToken, platform }, { headers }
    ).pipe(map(() => void 0));
  }

  getAdminQueue(token: string): Observable<{ queue: AdminQueueEntry[] }> {
    const headers = new HttpHeaders({ Authorization: `Bearer ${token}` });
    return this.http.get<ApiResponse<{ queue: AdminQueueEntry[] }>>(`${this.base}/admin-queue`, { headers }).pipe(map(r => r.data));
  }

  updateQueueStatus(token: string, num: number, status: string): Observable<void> {
    const headers = new HttpHeaders({ Authorization: `Bearer ${token}` });
    return this.http.put<ApiResponse<void>>(`${this.base}/admin-queue/${num}`, { status }, { headers }).pipe(map(() => void 0));
  }
}

export interface AdminQueueEntry {
  num: number;
  first_name: string;
  last_name: string;
  mobile: string;
  email: string;
  customer_status: string;
  order_number: number;
  group_size: number;
  created: string;
  updated: string;
}
